import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import { AssignRunner, AssignRunnerVariables } from "./types/AssignRunner";
import { OpsScanParcel, OpsScanParcelVariables } from "./types/OpsScanParcel";
import { OrderLineAdd, OrderLineAddVariables } from "./types/OrderLineAdd";
import {
  OrderLineRemove,
  OrderLineRemoveVariables
} from "./types/OrderLineRemove";
import {
  OrderLineUpdateQuantity,
  OrderLineUpdateQuantityVariables
} from "./types/OrderLineUpdateQuantity";
import {
  OrderLineUpdateStatus,
  OrderLineUpdateStatusVariables
} from "./types/OrderLineUpdateStatus";
import {
  RevertOrderStatus,
  RevertOrderStatusVariables
} from "./types/RevertOrderStatus";
import {
  RunnerScanParcel,
  RunnerScanParcelVariables
} from "./types/RunnerScanParcel";
import { SetParcelNo, SetParcelNoVariables } from "./types/SetParcelNo";
import {
  StoreOrderUpdate,
  StoreOrderUpdateVariables
} from "./types/StoreOrderUpdate";

const storeOrderUpdateMutation = gql`
  mutation StoreOrderUpdate(
    $id: ID!
    $smopStatus: SmopOrderStatusEnum!
    $cancelReason: String
    $overrideReason: String
  ) {
    updateStoreOrderStatus(
      id: $id
      smopStatus: $smopStatus
      cancelReason: $cancelReason
      overrideReason: $overrideReason
    ) {
      errors: orderErrors {
        code
        field
        message
      }
      storeOrder {
        id
        orderCode
        smopStatus
      }
    }
  }
`;
export const TypedStoreOrderUpdateMutation = TypedMutation<
  StoreOrderUpdate,
  StoreOrderUpdateVariables
>(storeOrderUpdateMutation);

export const useStoreOrderUpdateMutation = makeMutation<
  StoreOrderUpdate,
  StoreOrderUpdateVariables
>(storeOrderUpdateMutation);

const assignStoreOrderRunner = gql`
  mutation AssignRunner($runnerId: ID!, $storeOrderId: ID!) {
    assignStoreOrderRunner(runnerId: $runnerId, storeOrderId: $storeOrderId) {
      storeOrder {
        id
        orderCode
        runner {
          id
          email
          firstName
          lastName
          contactNo
        }
      }
      orderErrors {
        code
        field
        message
      }
    }
  }
`;

export const useAssignRunnerMutation = makeMutation<
  AssignRunner,
  AssignRunnerVariables
>(assignStoreOrderRunner);

const storeOrderSetParcelNo = gql`
  mutation SetParcelNo($id: ID!, $parcelNo: String!) {
    storeOrderSetParcelNo(id: $id, parcelNo: $parcelNo) {
      storeOrder {
        id
        orderCode
        parcelNo
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useSetParcelNo = makeMutation<SetParcelNo, SetParcelNoVariables>(
  storeOrderSetParcelNo
);

const storeOrderRunnerScanParcel = gql`
  mutation RunnerScanParcel($id: ID!, $parcelNo: String!) {
    storeOrderRunnerScanParcel(id: $id, parcelNo: $parcelNo) {
      storeOrder {
        id
        orderCode
        parcelNo
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useRunnerScanParcel = makeMutation<
  RunnerScanParcel,
  RunnerScanParcelVariables
>(storeOrderRunnerScanParcel);

const storeOrderOpsAdminScanParcel = gql`
  mutation OpsScanParcel($id: ID!, $parcelNo: String!) {
    storeOrderOpsAdminScanParcel(id: $id, parcelNo: $parcelNo) {
      storeOrder {
        id
        orderCode
        parcelNo
      }
      event {
        id
        date
        type
        user {
          firstName
        }
        message
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOpsAdminScanParcel = makeMutation<
  OpsScanParcel,
  OpsScanParcelVariables
>(storeOrderOpsAdminScanParcel);

const storeOrderLineDelete = gql`
  mutation OrderLineRemove($storeOrderId: ID!, $lineIds: [ID]!) {
    storeOrderLineDelete(storeOrderId: $storeOrderId, lineIds: $lineIds) {
      storeOrder {
        id
        lines {
          id
          productName
          status
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderLineRemove = makeMutation<
  OrderLineRemove,
  OrderLineRemoveVariables
>(storeOrderLineDelete);

const storeorderLineAdd = gql`
  mutation OrderLineAdd(
    $storeOrderId: ID!
    $lines: [StoreOrderLineInput]!
    $tenantId: ID!
  ) {
    storeOrderLinesAdd(
      storeOrderId: $storeOrderId
      lines: $lines
      tenantId: $tenantId
    ) {
      storeOrder {
        lines {
          id
          productName
          variantName
          quantity
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderLineAdd = makeMutation<
  OrderLineAdd,
  OrderLineAddVariables
>(storeorderLineAdd);

const storeOrderPickupLine = gql`
  mutation OrderLineUpdateStatus(
    $lineIds: [ID]!
    $status: OrderLineStatusEnum!
    $storeOrderId: ID!
  ) {
    storeOrderPickLine(
      lineIds: $lineIds
      status: $status
      storeOrderId: $storeOrderId
    ) {
      storeOrder {
        lines {
          id
          productName
          variantName
          quantity
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderLineUpdateStatus = makeMutation<
  OrderLineUpdateStatus,
  OrderLineUpdateStatusVariables
>(storeOrderPickupLine);

const storeOrderLineUpdateQuantity = gql`
  mutation OrderLineUpdateQuantity(
    $lines: [StoreOrderLineUpdateQuantityInput]!
    $storeOrderId: ID!
  ) {
    storeOrderLineUpdateQuantity(lines: $lines, storeOrderId: $storeOrderId) {
      storeOrder {
        id
        orderCode
        lines {
          id
          productName
          quantity
        }
      }
      orderErrors {
        field
        message
        code
        orderLine
      }
    }
  }
`;

export const useOrderLineUpdateQuantity = makeMutation<
  OrderLineUpdateQuantity,
  OrderLineUpdateQuantityVariables
>(storeOrderLineUpdateQuantity);

const storeOrderRevertFromCancelled = gql`
  mutation RevertOrderStatus($id: ID!, $revertReason: String!) {
    storeOrderRevertFromCancelled(id: $id, revertReason: $revertReason) {
      storeOrder {
        id
        orderCode
        smopStatus
      }
      orderErrors {
        field
        message
        code
        orderLine
      }
    }
  }
`;

export const useOrderRevertFromCancelled = makeMutation<
  RevertOrderStatus,
  RevertOrderStatusVariables
>(storeOrderRevertFromCancelled);
